import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { ReportView } from '../../../components/ui';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { fetcher } from '../../../services/fetcher';
import { ComplianceReport, type ComplianceReportData } from './ComplianceReport';
import { useManagementCompanyLogoBlobUrl } from './useManagementCompanyLogo';

const ComplianceReportPage: React.FC = () => {
  const { colors } = useTheme();
  const [params] = useSearchParams();
  const vesselId = params.get('vesselId')?.trim() || '';
  const periodFrom = params.get('periodFrom')?.trim() || '';
  const periodTo = params.get('periodTo')?.trim() || '';

  const key =
    vesselId && periodFrom && periodTo
      ? `/wrh/compliance?vesselId=${encodeURIComponent(vesselId)}&periodFrom=${encodeURIComponent(periodFrom)}&periodTo=${encodeURIComponent(periodTo)}`
      : null;

  const { data, error, isLoading } = useSWR<ComplianceReportData>(key, fetcher, {
    revalidateOnFocus: false,
  });
  const { logoBlobUrl: companyLogoSrc } = useManagementCompanyLogoBlobUrl(
    data?.managementCompany?.id,
    data?.managementCompany?.logoStorageKey,
  );

  const backHref = useMemo(() => {
    if (!vesselId || !periodFrom || !periodTo) return '/wrh/compliance';
    return `/wrh/compliance?vesselId=${encodeURIComponent(vesselId)}&periodFrom=${encodeURIComponent(periodFrom)}&periodTo=${encodeURIComponent(periodTo)}`;
  }, [vesselId, periodFrom, periodTo]);

  if (!vesselId || !periodFrom || !periodTo) {
    return (
      <div className={cn('flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4', colors.background)}>
        <p className={cn('text-sm', colors.textMuted)}>Select vessel and period on the compliance page first.</p>
        <Link to="/wrh/compliance" className={cn('text-sm font-medium', colors.primaryFg, 'hover:underline')}>
          ← Back to compliance
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={cn('flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4', colors.background)}>
        <p className={cn('text-sm', colors.textMuted)}>Could not load compliance report.</p>
        <Link to={backHref} className={cn('text-sm font-medium', colors.primaryFg, 'hover:underline')}>
          ← Back to compliance
        </Link>
      </div>
    );
  }

  const nc = data.rows?.length ?? 0;

  return (
    <ReportView
      backTo={backHref}
      backLabel="Back to compliance"
      title="WRH compliance"
      subtitle={
        <>
          {data.vesselName || 'Vessel'}
          {data.vesselAbrv ? ` (${data.vesselAbrv})` : ''} · {formatDateGlobal(data.periodFrom)} –{' '}
          {formatDateGlobal(data.periodTo)} · {nc} NC
        </>
      }
      printPageSize="A4 portrait"
      printMargin="0"
    >
      <ComplianceReport data={data} companyLogoSrc={companyLogoSrc} />
    </ReportView>
  );
};

export default ComplianceReportPage;
